import { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Routes,
  Route,
} from "react-router-dom";
import { AppProvider } from "./contexts/AppProvider";
import ProtectedRoute from "./components/ProtectedRoute";
import { SalsaAnalyticsTracker } from "./components/SalsaAnalyticsTracker";
import { DashboardProductTour } from "./components/DashboardProductTour";

{
  /* App Pages */
}
import Home from "./pages/marketing/Landing";

{
  /* Marketing Pages */
}
const Agents = lazy(() => import("./pages/Agents"));
const Billing = lazy(() => import("./pages/Billing"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DemoRedirect = lazy(() => import("./pages/DemoRedirect"));
const TonysPizzaDemo = lazy(() => import("./pages/TonysPizzaDemo"));
const LoginPage = lazy(() => import("./pages/Login"));
const InstallWidget = lazy(() => import("./pages/InstallWidget"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Profile = lazy(() => import("./pages/Profile"));
const Register = lazy(() => import("./pages/Register"));
const Settings = lazy(() => import("./pages/Settings"));
const Signup = lazy(() => import("./pages/Signup"));
const Tours = lazy(() => import("./pages/Tours"));
const Appointments = lazy(() => import("./pages/Appointments"));
const GrowthCampaigns = lazy(() => import("./pages/GrowthCampaigns"));
const VoiceAIDemo = lazy(() => import("./pages/VoiceAIDemo"));
const Widgets = lazy(() => import("./pages/Widgets"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const About = lazy(() => import("./pages/marketing/About"));
const AiAgent = lazy(() => import("./pages/marketing/AiAgent"));
const Blog = lazy(() => import("./pages/marketing/Blog"));
const BusinessAnalytics = lazy(
  () => import("./pages/marketing/BusinessAnalytics"),
);
const Careers = lazy(() => import("./pages/marketing/Careers"));
const Contact = lazy(() => import("./pages/marketing/Contact"));
const Documentation = lazy(() => import("./pages/marketing/Documentation"));
const McpDocumentation = lazy(
  () => import("./pages/marketing/McpDocumentation"),
);
const Privacy = lazy(() => import("./pages/marketing/Privacy"));
const ProcessAutomation = lazy(
  () => import("./pages/marketing/ProcessAutomation"),
);
const Security = lazy(() => import("./pages/marketing/Security"));
const SystemStatus = lazy(() => import("./pages/marketing/SystemStatus"));
const Terms = lazy(() => import("./pages/marketing/Terms"));
const CookiesPolicy = lazy(() => import("./pages/marketing/CookiesPolicy"));
const Solutions = lazy(() => import("./pages/marketing/Solutions"));
const Pricing = lazy(() => import("./pages/marketing/Pricing"));
const BetaSignup = lazy(() => import("./pages/marketing/BetaSignup"));
const TourDemo = lazy(() => import("./pages/marketing/TourDemo"));
const CapturePair = lazy(() => import("./pages/marketing/CapturePair"));
const MarketingNotFound = lazy(
  () => import("./pages/marketing/MarketingNotFound"),
);
const VisualizerDemo = lazy(() => import("./pages/VisualizerDemo"));
const VisualizerPlayground = lazy(() => import("./pages/VisualizerPlayground"));
const V1Index = lazy(() => import("./pages/v1"));
const V1Agent = lazy(() => import("./pages/v1/Agent"));
const V1AuthPage = lazy(() => import("./pages/v1/AuthPage"));
const V1Billing = lazy(() => import("./pages/v1/Billing"));
const V1Dashboard = lazy(() => import("./pages/v1/Dashboard"));
const V1Home = lazy(() => import("./pages/v1/Home"));
const V1OriginalDashboard = lazy(() => import("./pages/v1/OriginalDashboard"));
const V1Profile = lazy(() => import("./pages/v1/Profile"));
const V1TrueDashboard = lazy(() => import("./pages/v1/TrueDashboard"));
const V3ReviewHub = lazy(() => import("./pages/v3/ReviewHub"));
const V3DesignSystem = lazy(() => import("./pages/v3/DesignSystem"));
const V1LayoutRemix = lazy(() => import("./pages/v3/V1LayoutRemix"));
const V3WidgetCode = lazy(() => import("./pages/v3/WidgetCode"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const EmailManagement = lazy(() => import("./pages/admin/EmailManagement"));
const ApiKeysManagement = lazy(() => import("./pages/admin/ApiKeysManagement"));

import SimpleBar from "simplebar-react";
import { demoRoutesEnabled } from "./utils/demoMode";
import "simplebar-react/dist/simplebar.min.css";
import "./App.css";

function AppRoutes() {
  const reviewRoutesEnabled =
    import.meta.env.DEV || import.meta.env.VITE_ENABLE_REVIEW_ROUTES === "true";
  const canUseDemoRoutes = demoRoutesEnabled;
  const adminRoutesEnabled =
    import.meta.env.DEV || import.meta.env.VITE_ENABLE_ADMIN_ROUTES === "true";

  return (
    <SimpleBar style={{ height: "100vh" }} autoHide={true}>
      <div className="App">
        <Suspense
          fallback={
            <div
              className="flex min-h-screen items-center justify-center bg-background text-foreground"
              role="status"
            >
              Loading Onzoe…
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<Register />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/about" element={<About />} />
            <Route path="/automation" element={<ProcessAutomation />} />
            <Route path="/security" element={<Security />} />
            <Route path="/status" element={<SystemStatus />} />
            <Route path="/docs" element={<Documentation />} />
            <Route path="/docs/mcp" element={<McpDocumentation />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/ai-agents" element={<AiAgent />} />
            <Route path="/analytics" element={<BusinessAnalytics />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookies" element={<CookiesPolicy />} />
            <Route path="/solutions" element={<Solutions />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/beta" element={<BetaSignup />} />
            <Route path="/demo/tour" element={<TourDemo />} />
            <Route path="/capture/pair" element={<CapturePair />} />
            <Route path="/visualizers" element={<VisualizerDemo />} />
            <Route path="/playground" element={<VisualizerPlayground />} />
            <Route
              path="/demo-user-dashboard.html"
              element={
                demoRoutesEnabled ? (
                  <DemoRedirect />
                ) : (
                  <Navigate to="/beta" replace />
                )
              }
            />
            {canUseDemoRoutes && (
              <>
                <Route path="/demo/tonys-pizza" element={<TonysPizzaDemo />} />
                <Route path="/voice-ai-demo" element={<VoiceAIDemo />} />
              </>
            )}

            {reviewRoutesEnabled && (
              <>
                {/* V1 comparison and legacy dashboard routes */}
                <Route path="/v1" element={<V1Index />} />
                <Route
                  path="/v1/true-dashboard"
                  element={<V1TrueDashboard />}
                />
                <Route path="/v1/dashboard" element={<V1Dashboard />} />
                <Route
                  path="/v1/original-dashboard"
                  element={<V1OriginalDashboard />}
                />
                <Route path="/v1/billing" element={<V1Billing />} />
                <Route path="/v1/agent" element={<V1Agent />} />
                <Route path="/v1/profile" element={<V1Profile />} />
                <Route path="/v1/home" element={<V1Home />} />
                <Route
                  path="/v1/auth"
                  element={<V1AuthPage onAuthSuccess={() => undefined} />}
                />

                {/* V3 review workspace */}
                <Route path="/v3" element={<V3ReviewHub />} />
                <Route path="/v3/design-system" element={<V3DesignSystem />} />
                <Route path="/v3/v1-layout-remix" element={<V1LayoutRemix />} />
                <Route path="/v3/widget-code" element={<V3WidgetCode />} />
              </>
            )}

            {adminRoutesEnabled && (
              <>
                {/* Admin routes */}
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/email" element={<EmailManagement />} />
                <Route path="/admin/api-keys" element={<ApiKeysManagement />} />
                <Route path="/admin/users" element={<AdminDashboard />} />
                <Route
                  path="/admin/subscriptions"
                  element={<AdminDashboard />}
                />
              </>
            )}

            {/* Protected routes */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <Onboarding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agents"
              element={
                <ProtectedRoute>
                  <Agents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing"
              element={
                <ProtectedRoute>
                  <Billing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/widgets"
              element={
                <ProtectedRoute>
                  <Widgets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/install"
              element={
                <ProtectedRoute>
                  <InstallWidget />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tours"
              element={
                <ProtectedRoute>
                  <Tours />
                </ProtectedRoute>
              }
            />
            <Route
              path="/appointments"
              element={
                <ProtectedRoute>
                  <Appointments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/growth"
              element={
                <ProtectedRoute>
                  <GrowthCampaigns />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<MarketingNotFound />} />
          </Routes>
        </Suspense>
      </div>
    </SimpleBar>
  );
}

function App() {
  return (
    <AppProvider>
      <Router>
        <SalsaAnalyticsTracker />
        <DashboardProductTour />
        <AppRoutes />
      </Router>
    </AppProvider>
  );
}

export default App;
